import { useEffect } from "react";
import { Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../lib/reveal";
import { resources } from "../data/resources";
import { formatDate } from "../data/resources";
import { generateTenderPdf } from "../lib/tenderPdf";
import { ArrowUpRight, CalendarClock, FileText, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function Resources() {
  useLenis();
  const { t } = useLang();
  useSeo({
    title: "Resource Hub — Technical Notes on Odour, Methane, Bio-mining & RDF | Fresh Earth",
    description:
      "Short, practical technical notes on landfill odour, methane reduction, bio-mining and RDF odour control — the biology behind cleaner waste sites.",
    path: "/resources",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [feature, ...rest] = resources;

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="resources-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="flex items-center gap-3 mb-6">
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("Resource Hub", "संसाधन केंद्र")}</span>
            <span data-testid="cadence-badge" className="inline-flex items-center gap-1.5 text-[0.68rem] tracking-[0.12em] uppercase text-terracotta border border-terracotta/40 rounded-full px-3 py-1">
              <CalendarClock size={12} /> {t("New note every month", "हर माह नई टिप्पणी")}
            </span>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl">{t("The biology behind cleaner waste sites.", "स्वच्छ अपशिष्ट स्थलों के पीछे का जीव विज्ञान।")}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate max-w-2xl">{t("Short, practical notes for municipal engineers, operators and industry — grounded in field results.", "नगरपालिका अभियंताओं, ऑपरेटरों व उद्योग के लिए संक्षिप्त, व्यावहारिक टिप्पणियाँ — फ़ील्ड परिणामों पर आधारित।")}</p>
          </Reveal>

          {/* Tender / spec kit */}
          <Reveal delay={0.15}>
            <div data-testid="tender-kit" className="mt-14 grain relative overflow-hidden bg-forest text-bone rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
              <div className="relative">
                <div className="flex items-center gap-2 text-terracotta mb-4">
                  <FileText size={18} />
                  <span className="text-xs tracking-[0.16em] uppercase font-semibold">{t("Procurement", "खरीद")}</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight">{t("The OED Tender & Spec Kit.", "OED टेंडर व स्पेसिफिकेशन किट।")}</h2>
                <p className="mt-4 text-bone/70 leading-relaxed max-w-xl">
                  {t(
                    "A two-page specification and ready-to-paste procurement clauses — product spec, performance thresholds, certifications and suggested tender language for RFP documents.",
                    "दो-पृष्ठ का विनिर्देश व तैयार-पेस्ट खरीद खंड — उत्पाद स्पेक, प्रदर्शन सीमाएँ, प्रमाणन व RFP दस्तावेज़ों हेतु सुझाई गई टेंडर भाषा।"
                  )}
                </p>
              </div>
              <button
                onClick={generateTenderPdf}
                data-testid="tender-download"
                className="relative inline-flex items-center gap-2 rounded-full bg-bone text-forest text-sm tracking-[0.08em] uppercase px-7 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 whitespace-nowrap"
              >
                <Download size={16} /> {t("Download spec sheet", "स्पेक शीट डाउनलोड करें")}
              </button>
            </div>
          </Reveal>

          {/* Feature */}
          <Reveal delay={0.15}>
            <Link to={`/resources/${feature.slug}`} data-testid={`resource-card-${feature.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mt-16 bg-sand rounded-3xl border border-stone/20 overflow-hidden hover:border-moss transition-colors duration-400">
              <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
                <img src={feature.image} alt={feature.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 lg:p-12">
                <div className="text-xs tracking-[0.14em] uppercase text-terracotta font-semibold">{feature.category} · {feature.readTime} · {formatDate(feature.date)}</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">{t(feature.title, feature.hi?.title)}</h2>
                <p className="text-base text-slate mt-4">{t(feature.dek, feature.hi?.dek)}</p>
                <span className="inline-flex items-center gap-2 text-sm tracking-[0.08em] uppercase text-moss mt-6">{t("Read note", "टिप्पणी पढ़ें")} <ArrowUpRight size={16} /></span>
              </div>
            </Link>
          </Reveal>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5 pb-28">
            {rest.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link to={`/resources/${r.slug}`} data-testid={`resource-card-${r.slug}`} className="group block h-full bg-bone rounded-3xl border border-stone/20 overflow-hidden hover:border-moss transition-colors duration-400">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <div className="text-xs tracking-[0.12em] uppercase text-terracotta font-semibold">{r.category} · {r.readTime} · {formatDate(r.date)}</div>
                    <h3 className="font-serif text-2xl mt-2 leading-tight text-ink">{t(r.title, r.hi?.title)}</h3>
                    <p className="text-sm text-slate mt-3">{t(r.dek, r.hi?.dek)}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
